import React from "react";
import {
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  ActionIcon,
  Skeleton,
  Pagination,
} from "@mantine/core";
import { IconMail, IconMailOpened } from "@tabler/icons-react";
import { useVoiceAI } from "../../context/VoiceAIContext";
import { formatPhoneNumberDisplay as formatPhoneNumber } from "../../utils/phoneUtils";

function formatDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function MessagesTab() {
  const {
    messages,
    messagesLoading,
    messagesPagination,
    unreadMessageCount,
    fetchMessages,
    markMessageRead,
  } = useVoiceAI();

  // Initial fetchMessages() runs in VoiceAIPage on mount; no need to repeat
  // here. Pagination changes still flow through fetchMessages(page) below.

  if (messagesLoading && messages.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={80} />
        <Skeleton height={80} />
        <Skeleton height={80} />
      </Stack>
    );
  }

  if (messages.length === 0) {
    return (
      <Paper p="xl" withBorder ta="center">
        <Text c="dimmed">
          No messages yet. Messages taken by the AI will appear here.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {unreadMessageCount > 0 && (
        <Group>
          <Badge color="red" variant="filled" size="lg">
            {unreadMessageCount} unread
          </Badge>
        </Group>
      )}

      {messages.map((msg) => (
        <Paper
          key={msg.id}
          p="md"
          withBorder
          style={{
            borderLeft: msg.is_read
              ? undefined
              : "3px solid var(--mantine-color-blue-6)",
          }}
        >
          <Group justify="space-between" align="flex-start">
            <div style={{ flex: 1 }}>
              <Group gap="xs" mb={4}>
                <Text fw={msg.is_read ? 400 : 600} size="sm">
                  {msg.caller_name || "Unknown Caller"}
                </Text>
                {msg.caller_phone && (
                  <Text size="xs" c="dimmed">
                    {formatPhoneNumber(msg.caller_phone)}
                  </Text>
                )}
              </Group>
              <Text size="sm" mb={4}>
                {msg.message}
              </Text>
              <Text size="xs" c="dimmed">
                {formatDate(msg.created_at)}
              </Text>
            </div>
            <ActionIcon
              variant="subtle"
              color={msg.is_read ? "gray" : "blue"}
              onClick={() => markMessageRead(msg.id, !msg.is_read)}
              title={msg.is_read ? "Mark as unread" : "Mark as read"}
            >
              {msg.is_read ? (
                <IconMailOpened size={18} />
              ) : (
                <IconMail size={18} />
              )}
            </ActionIcon>
          </Group>
        </Paper>
      ))}

      {messagesPagination.total_pages > 1 && (
        <Group justify="center">
          <Pagination
            total={messagesPagination.total_pages}
            value={messagesPagination.page}
            onChange={(page) => fetchMessages(page)}
          />
        </Group>
      )}
    </Stack>
  );
}
